import { useState } from 'react'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Calendar, Hash, Phone, Database, Gauge, Type } from 'lucide-react'
import { Heading, Text } from '@/components/ui/typography'
import { MetricCard } from '@/components/ui/metric-card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useVoicePipelineData } from '@/hooks/useVoicePipelineData'
import { useVoicePipelineSessions } from '@/hooks/useVoicePipelineSessions'
import { useAppAuth } from '@/contexts/app-auth.context'
import { VoicePipelineKPICards } from '@/components/voice-pipeline/VoicePipelineKPICards'
import { LatencyTrendChart } from '@/components/voice-pipeline/LatencyTrendChart'
import { TokenTrendChart } from '@/components/voice-pipeline/TokenTrendChart'
import { ModelComparisonTable } from '@/components/voice-pipeline/ModelComparisonTable'
import { LatencyDistributionChart } from '@/components/voice-pipeline/LatencyDistributionChart'
import { SessionsTable } from '@/components/voice-pipeline/SessionsTable'
import { VOICE_PIPELINE_LANGUAGES } from '@super/shared-types'

export function VoicePipelinePage() {
  const {
    dashboardData,
    isFetching,
    error,
    dateRange,
    setDateRange,
    companyScope,
    setCompanyScope,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    appliedCustomDates,
    handleCustomDateRangeApply,
    handleCustomDateRangeCancel,
    language,
    setLanguage,
  } = useVoicePipelineData()

  const { isSuperAdmin, currentCompany } = useAppAuth()
  const [customDateRangeOpen, setCustomDateRangeOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'sessions'>(
    'overview',
  )

  const effectiveCompanyId =
    companyScope === 'selected' ? currentCompany?.id : undefined

  const {
    sessionsData,
    isFetching: isSessionsFetching,
    error: sessionsError,
    page,
    setPage,
    sortBy,
    sortDir,
    handleSort,
  } = useVoicePipelineSessions({
    dateRange,
    appliedCustomDates,
    companyId: effectiveCompanyId,
    language,
    enabled: activeTab === 'sessions',
  })

  useDocumentHead({
    title: 'Performance',
    description: 'Voice agent pipeline performance metrics',
  })

  const summary = dashboardData?.summary ?? null
  const displayError = activeTab === 'overview' ? error : sessionsError

  return (
    <div className="bg-background w-full pt-16">
      <div className="flex flex-col gap-6 px-4 sm:px-6 pt-4 sm:pt-6 pb-20 sm:pb-20 w-full box-border">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0 animate-in fade-in duration-500 w-full">
          <div className="flex flex-col gap-1">
            <Heading variant="subsection-title">Performance</Heading>
            <Text variant="muted">
              Aggregated voice agent performance metrics
            </Text>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {/* Company scope toggle */}
            {isSuperAdmin && (
              <Tabs
                value={companyScope}
                onValueChange={v => setCompanyScope(v as 'all' | 'selected')}
              >
                <TabsList mobileDropdown={false}>
                  <TabsTrigger value="all">All Companies</TabsTrigger>
                  <TabsTrigger value="selected">This Company</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            <Select
              value={language || 'all'}
              onValueChange={v => setLanguage(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-full sm:w-36 h-9 border-border hover:border-muted-foreground/40 transition-all">
                <SelectValue placeholder="All Languages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Languages</SelectItem>
                {Object.values(VOICE_PIPELINE_LANGUAGES).map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={dateRange === 'custom' ? 'custom' : dateRange}
              onValueChange={value => {
                if (value !== 'custom') {
                  setDateRange(value)
                  setCustomStartDate('')
                  setCustomEndDate('')
                }
              }}
            >
              <SelectTrigger className="w-full sm:min-w-[200px] sm:max-w-[260px] h-9 transition-all hover:border-muted-foreground/40 border-border">
                <SelectValue placeholder="Select range" className="truncate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="48h">Last 48 hours</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                {/* Wider ranges are company-scoped only: cross-company scans are capped server-side */}
                {companyScope !== 'all' && (
                  <>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                  </>
                )}
                {appliedCustomDates && (
                  <SelectItem value="custom" className="whitespace-nowrap">
                    {`${new Date(appliedCustomDates.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(appliedCustomDates.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {companyScope !== 'all' && (
              <Popover
                open={customDateRangeOpen}
                onOpenChange={setCustomDateRangeOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 px-3 border-border"
                  >
                    <Calendar className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-80 sm:w-80"
                  align="end"
                  sideOffset={8}
                >
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <Text variant="body" weight="medium">
                        Custom Date Range
                      </Text>
                      <Text variant="muted">Select start and end dates</Text>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label
                          htmlFor="vp-start-date"
                          className="text-xs font-medium"
                        >
                          Start Date
                        </Label>
                        <Input
                          id="vp-start-date"
                          type="date"
                          value={customStartDate}
                          onChange={e => setCustomStartDate(e.target.value)}
                          max={customEndDate || undefined}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="vp-end-date"
                          className="text-xs font-medium"
                        >
                          End Date
                        </Label>
                        <Input
                          id="vp-end-date"
                          type="date"
                          value={customEndDate}
                          onChange={e => setCustomEndDate(e.target.value)}
                          min={customStartDate || undefined}
                          max={new Date().toISOString().split('T')[0]}
                          className="h-9"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          handleCustomDateRangeCancel()
                          setCustomDateRangeOpen(false)
                        }}
                        className="h-8"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          handleCustomDateRangeApply()
                          setCustomDateRangeOpen(false)
                        }}
                        disabled={!customStartDate || !customEndDate}
                        className="h-8"
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Page-level tabs */}
        <Tabs
          value={activeTab}
          onValueChange={v => setActiveTab(v as 'overview' | 'sessions')}
        >
          <TabsList mobileDropdown={false}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sessions">Sessions</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="flex flex-col gap-6 mt-2">
              {/* KPI Cards */}
              <VoicePipelineKPICards summary={summary} isLoading={isFetching} />

              {/* Charts Grid */}
              <div className="grid gap-6 lg:grid-cols-2">
                <LatencyTrendChart
                  data={dashboardData?.sessionTrend ?? []}
                  totalSessionCount={dashboardData?.totalSessionCount}
                  isLoading={isFetching}
                />
                <TokenTrendChart
                  data={dashboardData?.sessionTrend ?? []}
                  totalSessionCount={dashboardData?.totalSessionCount}
                  isLoading={isFetching}
                />
              </div>

              {/* Latency Distribution */}
              <LatencyDistributionChart
                data={dashboardData?.latencyDistribution}
                isLoading={isFetching}
                bucketWidthMs={dashboardData?.latencyBucketWidthMs}
                bucketCount={dashboardData?.latencyBucketCount}
                rangeStartMs={dashboardData?.latencyRangeStartMs}
              />

              {/* Model Comparison Table */}
              <ModelComparisonTable
                sttData={dashboardData?.sttModelComparison ?? []}
                llmData={dashboardData?.llmModelComparison ?? []}
                ttsData={dashboardData?.ttsModelComparison ?? []}
                isLoading={isFetching}
              />

              {/* Secondary KPIs */}
              {summary && !isFetching && (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5 animate-in fade-in duration-300">
                  <MetricCard
                    icon={Hash}
                    title="Total Turns"
                    metric={summary.totalTurns.toLocaleString()}
                    description="Turn count in period"
                  />
                  <MetricCard
                    icon={Phone}
                    title="Total Calls"
                    metric={summary.totalCalls.toLocaleString()}
                    description="Distinct calls in period"
                  />
                  <MetricCard
                    icon={Database}
                    title="Cache Hit Rate"
                    metric={`${(summary.cacheHitRate * 100).toFixed(1)}%`}
                    description="Prompt tokens served from cache"
                    info="Higher cache hit rate = lower cost and faster TTFT"
                  />
                  <MetricCard
                    icon={Gauge}
                    title="Avg Tokens/sec"
                    metric={summary.avgLlmTokensPerSecond.toFixed(1)}
                    description="LLM generation throughput"
                  />
                  <MetricCard
                    icon={Type}
                    title="Total TTS Chars"
                    metric={summary.totalTtsChars.toLocaleString()}
                    description="Characters synthesized (cost driver)"
                  />
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sessions">
            <div className="mt-2">
              <SessionsTable
                sessions={sessionsData?.sessions ?? []}
                totalCount={sessionsData?.totalCount ?? 0}
                page={page}
                pageSize={sessionsData?.pageSize ?? 25}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                onPageChange={setPage}
                isLoading={isSessionsFetching}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Error display */}
        {displayError && (
          <Card className="border-red-200 bg-red-50 animate-in fade-in slide-in-from-top-4 duration-300">
            <CardContent className="pt-6">
              <p className="text-red-600">{displayError}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
